import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Intrinsic width of a node (ignores current flex shrink / w-full), via off-screen clone.
 */
function measureIntrinsicWidth(node) {
  if (!node) return 0;
  const clone = node.cloneNode(true);
  clone.querySelectorAll('.w-full').forEach(n => n.classList.remove('w-full'));
  clone.classList.remove('w-full');
  clone.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'width:max-content',
    'max-width:none',
    'height:auto',
    'visibility:hidden',
    'pointer-events:none'
  ].join(';');
  document.body.appendChild(clone);
  const w = clone.getBoundingClientRect().width;
  clone.remove();
  return w;
}

/**
 * True when the two children cannot sit on one row at the current container width,
 * using intrinsic widths (not affected by w-full on flex items).
 * When `enabled` is false, always returns false.
 */
export default function useFlexRowWrapped(enabled) {
  const ref = useRef(null);
  const [wrapped, setWrapped] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      setWrapped(false);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const check = () => {
      const { flexDirection, columnGap, gap } = getComputedStyle(el);
      if (flexDirection !== 'row' && flexDirection !== 'row-reverse') {
        setWrapped(false);
        return;
      }

      const kids = el.children;
      if (kids.length < 2) {
        setWrapped(false);
        return;
      }

      const cw = el.clientWidth;
      if (cw <= 0) return;

      const gapPx =
        Number.parseFloat(columnGap) || Number.parseFloat(gap) || 16;
      const w0 = measureIntrinsicWidth(kids[0]);
      const w1 = measureIntrinsicWidth(kids[1]);
      const needsStack = w0 + w1 + gapPx > cw + 0.5;
      setWrapped(needsStack);
    };

    const ro = new ResizeObserver(() => check());
    ro.observe(el);
    check();

    return () => ro.disconnect();
  }, [enabled]);

  return [ref, wrapped];
}
